import type { SearchClient, SearchResponse } from '@algolia/client-search';
import { searchClient } from '@algolia/client-search';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { VISITOR_ID } from '@core/user-identification';
import type { SearchHit } from '@search/models';
import { firstValueFrom, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  KEYWORD_QUERYSTRING_NAME,
  MOST_SEARCHED_FOR_HITS,
  SEARCH_ANONYMOUS_USER_TOKEN,
  SEARCH_HITS_PER_PAGE,
} from './search.variables';

/**
 * Service responsible for handling search operations using Algolia's search API.
 * Provides functionality for executing searches, retrieving suggestions, and managing search-related state.
 *
 * @Injectable Provided in root scope for application-wide availability
 */
@Injectable({
  providedIn: 'root',
})
export class SearchService {
  readonly #route = inject(ActivatedRoute);
  /**
   * Observable that emits the current search query from URL parameters
   * @public
   */
  public searchQuery$: Observable<string> = this.#route.queryParams.pipe(
    map((params) => params[KEYWORD_QUERYSTRING_NAME])
  );
  readonly #algoliaConfig = inject(ENVIRONMENT_CONFIG).algoliaSearch;
  /**
   * Segment configuration for search filtering
   * @private
   */
  readonly #segment = inject(ENVIRONMENT_CONFIG).segment;
  /**
   * Visitor identification
   */
  readonly #visitorID = inject(VISITOR_ID);
  /**
   * Cache for most commonly suggested search items to minimize API calls
   * @private
   */
  #mostSuggested?: SearchResponse<SearchHit>;
  private readonly client: SearchClient = searchClient(
    this.#algoliaConfig.applicationId,
    this.#algoliaConfig.applicationKey
  );

  /**
   * Retrieves the current search query from URL parameters as a Promise
   * @returns Promise containing the current search query string
   */
  public getSearchQueryFromParams() {
    return firstValueFrom(this.searchQuery$);
  }

  /**
   * Executes a search query against the Algolia index
   *
   * @param query - The search term to query
   * @param page - The page number for pagination (0-based index)
   * @returns Promise with the search response
   *
   * @example
   * ```typescript
   * const results = await searchService.executeQuery('search term', 0);
   * ```
   */
  public async executeQuery(query: string, page = 0) {
    return this.client.searchSingleIndex<SearchHit>({
      indexName: this.#algoliaConfig.indexName,
      searchParams: {
        query,
        page,
        hitsPerPage: SEARCH_HITS_PER_PAGE,
        highlightPreTag: '<strong>',
        highlightPostTag: '</strong>',
        optionalFilters: [`_tags:${this.#segment}<score=2>`],
        userToken: (await this.#visitorID) ?? SEARCH_ANONYMOUS_USER_TOKEN,
        clickAnalytics: true,
      },
    });
  }

  /**
   * Retrieves query suggestions for autocomplete functionality
   *
   * @param query - The partial search term to get suggestions for
   * @returns Promise with suggestion results
   *
   * @example
   * ```typescript
   * const suggestions = await searchService.getQuerySuggestions('partial term');
   * ```
   */
  public getQuerySuggestions(query: string) {
    return this.client.searchSingleIndex<SearchHit>({
      indexName: this.#algoliaConfig.autoCompleteIndexName,
      searchParams: {
        query,
        hitsPerPage: 5,
        // 1 crying cat for bootstrap hardcoding :(
        highlightPreTag: '<span class="fw-normal">',
        highlightPostTag: '</span>',
      },
    });
  }

  /**
   * Retrieves the most common search suggestions, using a cached response if available
   *
   * @returns Promise with the most common search suggestions
   *
   * @example
   * ```typescript
   * const commonSuggestions = await searchService.getMostCommonSuggestions();
   * ```
   */
  public async getMostCommonSuggestions() {
    // Only retrieve data from algolia when no data is cached.
    if (!this.#mostSuggested) {
      this.#mostSuggested = await this.client.searchSingleIndex<SearchHit>({
        indexName: this.#algoliaConfig.autoCompleteIndexName,
        searchParams: {
          hitsPerPage: MOST_SEARCHED_FOR_HITS,
        },
      });
    }
    return this.#mostSuggested;
  }
}
